import React from "react";
import PropTypes from "prop-types";
import { Download, FileText, RefreshCw } from "lucide-react";
import moment from "moment";

import Button from "./Button";
import EmptyState from "./EmptyState";

const readableSize = (bytes) => {
  if (!Number(bytes)) return null;
  const units = ["Bytes", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return (bytes / (1024 ** index)).toFixed(index ? 1 : 0) + " " + units[index];
};

const PackExportPanel = ({ generating = false, onDownload, onGenerate, pdf, preparing = false }) => (
  <section aria-labelledby="pack-export-title" className="wsm-pack-export">
    <div className="wsm-pack-export__header">
      <div>
        <h2 id="pack-export-title">Pack export</h2>
        <p>Generate and download the current PDF version of this worksite pack.</p>
        {preparing && <p role="status">Preparing the PDF. Large packs can take a few minutes, and you can keep using Worksite Manager while it finishes.</p>}
      </div>
      {pdf && <Button icon={RefreshCw} loading={generating} onClick={onGenerate} variant="outline">Regenerate PDF</Button>}
    </div>

    {!pdf ? (
      <EmptyState
        actions={<Button icon={FileText} loading={generating} onClick={onGenerate}>Generate PDF</Button>}
        description="Generate a PDF containing the latest pack information and forms."
        icon={FileText}
        title="No PDF generated"
      />
    ) : (
      <div className="wsm-pack-export__file">
        <span className="wsm-pack-export__icon"><FileText aria-hidden="true" /></span>
        <div>
          <strong>{pdf.name || pdf.filename || "Worksite pack.pdf"}</strong>
          <span>
            {readableSize(pdf.size)}
            {pdf.updated_at && (readableSize(pdf.size) ? " | " : "") + "Generated " + moment(pdf.updated_at).format("D MMM YYYY, HH:mm")}
          </span>
        </div>
        <Button disabled={!pdf.base64} icon={Download} onClick={onDownload}>Download PDF</Button>
      </div>
    )}

    {pdf?.failed_imports?.length > 0 && (
      <div className="wsm-pack-export__warning" role="alert">
        <strong>Some supporting files could not be included</strong>
        <ul>{pdf.failed_imports.map((item) => <li key={item?.id ?? String(item)}>{item?.name ?? String(item)}</li>)}</ul>
      </div>
    )}
  </section>
);

PackExportPanel.propTypes = {
  generating: PropTypes.bool,
  onDownload: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
  pdf: PropTypes.shape({
    base64: PropTypes.string,
    failed_imports: PropTypes.array,
    filename: PropTypes.string,
    name: PropTypes.string,
    size: PropTypes.number,
    updated_at: PropTypes.string,
  }),
  preparing: PropTypes.bool,
};

export default PackExportPanel;